/**
 * Session 8 Block 4 · Operator GDPR (DSGVO) actions per tenant.
 * Export (Art. 15) + Two-stage deletion (Art. 17).
 */
import React, { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription, AlertTitle }
  from "../../components/ui/alert";
import { AlertTriangle, Download, Trash2 } from "lucide-react";


export function GdprPanel({ tenantId, tenantSlug, tenantStatus,
                                 onStatusChange }) {
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [delReason, setDelReason] = useState("");
  const [delSlug, setDelSlug] = useState("");
  const [delAck, setDelAck] = useState("");
  const [delDays, setDelDays] = useState(30);

  const runExport = async () => {
    setExporting(true);
    try {
      const r = await api.post(
        `/operator/tenants/${tenantId}/gdpr-export`,
        { format: "zip", reason: "operator-initiated Art. 15 export" });
      setExportResult(r.data);
      toast.success("Export bereit.");
    } catch (e) {
      toast.error("Export fehlgeschlagen");
    } finally { setExporting(false); }
  };

  const scheduleDeletion = async () => {
    if (delSlug !== tenantSlug) {
      toast.error("Slug muss exakt übereinstimmen.");
      return;
    }
    try {
      await api.post(
        `/operator/tenants/${tenantId}/gdpr-delete`,
        { reason: delReason, confirm_slug: delSlug,
          confirm_ack: delAck,
          hard_delete_after_days: parseInt(delDays, 10) });
      toast.success("Löschung geplant.");
      setShowDelete(false);
      onStatusChange && onStatusChange("deletion_pending");
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || "Fehler");
    }
  };

  const cancelDeletion = async () => {
    try {
      await api.post(
        `/operator/tenants/${tenantId}/gdpr-cancel-deletion`,
        { reason: "operator cancelled within grace" });
      toast.success("Löschung storniert.");
      onStatusChange && onStatusChange("active");
    } catch (e) {
      toast.error("Fehler beim Stornieren.");
    }
  };

  return (
    <div className="space-y-4" data-testid="operator-gdpr-panel">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Download className="h-4 w-4" /> DSGVO Art. 15 · Datenexport
          </CardTitle>
          <CardDescription className="text-xs">
            Vollständiger Export aller Tenant-Daten (ZIP). Download-URL
            läuft nach 24h ab.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={runExport} disabled={exporting}
                   data-testid="operator-gdpr-export-btn">
            {exporting ? "Exportiere…" : "Export erstellen"}
          </Button>
          {exportResult && (
            <div className="mt-3 p-3 rounded border border-border
                            text-xs space-y-1">
              <div>Status: <code>{exportResult.status}</code></div>
              {exportResult.download_url && (
                <a href={exportResult.download_url}
                    className="text-primary underline break-all"
                    target="_blank" rel="noreferrer"
                    data-testid="operator-gdpr-download-link">
                  Datei herunterladen
                </a>
              )}
              <div>Ablauf: <code>{exportResult.expires_at}</code></div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2
                                text-rose-700">
            <Trash2 className="h-4 w-4" /> DSGVO Art. 17 · Löschung
          </CardTitle>
          <CardDescription className="text-xs">
            Zweistufig: Sofort in ``deletion_pending`` (Login blockiert),
            nach Grace-Period unwiderrufliche Löschung.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {tenantStatus === "deletion_pending" ? (
            <>
              <Alert data-testid="operator-gdpr-pending-alert">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Löschung ist geplant</AlertTitle>
                <AlertDescription>
                  Solange nicht endgültig gelöscht, kann der Vorgang
                  storniert werden.
                </AlertDescription>
              </Alert>
              <Button variant="outline" onClick={cancelDeletion}
                       data-testid="operator-gdpr-cancel-btn">
                Löschung stornieren
              </Button>
            </>
          ) : !showDelete ? (
            <Button variant="destructive"
                     onClick={() => setShowDelete(true)}
                     data-testid="operator-gdpr-schedule-btn">
              Löschung planen
            </Button>
          ) : (
            <div className="space-y-3">
              <div>
                <Label>Grund (Pflicht)</Label>
                <Input value={delReason}
                        onChange={(e) => setDelReason(e.target.value)}
                        data-testid="operator-gdpr-reason" />
              </div>
              <div>
                <Label>Grace-Period (Tage)</Label>
                <Input type="number" value={delDays}
                        onChange={(e) => setDelDays(e.target.value)}
                        min="1" max="365"
                        data-testid="operator-gdpr-days" />
              </div>
              <div>
                <Label>Slug bestätigen (
                  <code>{tenantSlug}</code>)</Label>
                <Input value={delSlug}
                        onChange={(e) => setDelSlug(e.target.value)}
                        data-testid="operator-gdpr-slug" />
              </div>
              <div>
                <Label>Bestätigung (enthält „irreversible")</Label>
                <Input value={delAck}
                        onChange={(e) => setDelAck(e.target.value)}
                        placeholder="I understand this is irreversible"
                        data-testid="operator-gdpr-ack" />
              </div>
              <div className="flex gap-2">
                <Button variant="destructive"
                         onClick={scheduleDeletion}
                         data-testid="operator-gdpr-confirm-btn">
                  Löschung bestätigen
                </Button>
                <Button variant="outline"
                         onClick={() => setShowDelete(false)}>
                  Abbrechen
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


export function MetadataSnapshotModal({ tenantId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    api.get(`/operator/tenants/${tenantId}/metadata-snapshot`)
       .then((r) => setData(r.data))
       .catch((e) => setError(
         e.response?.data?.detail?.error || "Fehler"));
  }, [tenantId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center
                     justify-center p-4"
          onClick={onClose}
          data-testid="operator-metadata-modal">
      <div className="bg-background border border-border rounded-lg
                       max-w-4xl w-full max-h-[85vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-border flex justify-between">
          <div>
            <h3 className="font-semibold text-lg">
              Metadaten-Ansicht (auditiert)
            </h3>
            <p className="text-xs text-muted-foreground">
              Nur Metadaten — keine Kampagnen-Inhalte, keine Chat-Texte.
              Dieser Zugriff wird im Audit-Log festgehalten.
            </p>
          </div>
          <Button variant="ghost" onClick={onClose}
                   data-testid="operator-metadata-close">
            Schließen
          </Button>
        </div>
        <div className="p-4 text-xs">
          {error && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Nicht verfügbar</AlertTitle>
              <AlertDescription>
                {error === "metadata_snapshot_disabled"
                  ? "Feature-Flag ist deaktiviert."
                  : error}
              </AlertDescription>
            </Alert>
          )}
          {data && (
            <pre className="overflow-auto whitespace-pre-wrap break-all"
                   data-testid="operator-metadata-content">
              {JSON.stringify(data, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
